import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { appointmentApi, leadApi, roomApi, userApi, googleCalendarApi } from '../lib/api';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { 
  format, 
  startOfMonth, endOfMonth, 
  startOfWeek, endOfWeek,
  addMonths, subMonths, 
  addWeeks, subWeeks,
  addDays, subDays,
  isSameDay 
} from 'date-fns';

// Import extracted components
import {
  CalendarHeader,
  CalendarGrid,
  CalendarDayDetails,
  CalendarWeekView,
  CalendarDayView,
  AppointmentDetailPanel,
  GoogleEventDetailPanel,
  CalendarLegend,
  NewAppointmentModal,
  EditAppointmentModal,
  CancelAppointmentModal
} from '../components/calendar';

// Team groups for meetings
const teamGroups = [
  { value: 'tecnico', label: 'Team Tecnico', roles: ['external_technician'] },
  { value: 'project_manager', label: 'Project Manager', roles: ['project_manager'] },
  { value: 'amministrativo', label: 'Amministrazione', roles: ['administration'] },
  { value: 'arredi', label: 'Team Arredi', roles: ['furniture_sales'] },
];

// Duration options
const durationOptions = [
  { value: 30, label: '30 minuti' },
  { value: 45, label: '45 minuti' },
  { value: 60, label: '1 ora' },
  { value: 90, label: '1 ora 30 min' },
  { value: 120, label: '2 ore' },
  { value: 180, label: '3 ore' },
  { value: 240, label: '4 ore' },
];

const CalendarPage = () => {
  const { user, isAdmin, isProjectManager, isTechnician, isAdministration, isFurnitureSales, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // View mode: 'day' | 'week' | 'month'
  const [viewMode, setViewMode] = useState('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date()); // Backwards compatibility
  
  const [appointments, setAppointments] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [leads, setLeads] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedCalendarUser, setSelectedCalendarUser] = useState('all');
  const [selectedDate, setSelectedDate] = useState(null);
  const [showNewAppointmentModal, setShowNewAppointmentModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Detail panel state
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [detailAppointment, setDetailAppointment] = useState(null);
  
  // Edit/Cancel state
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [editData, setEditData] = useState({ appointment_date: new Date(), send_email: true, reason: '' });
  const [cancelData, setCancelData] = useState({ send_email: true, reason: '' });
  
  // Google Calendar state
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleEvents, setGoogleEvents] = useState([]);
  const [syncingToGoogle, setSyncingToGoogle] = useState(null);
  const [syncingAll, setSyncingAll] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(null);
  
  const initialAppointmentState = {
    lead_id: '',
    assigned_technician_id: '',
    appointment_date: new Date(),
    appointment_type: 'sopralluogo',
    duration_minutes: 60,
    room_id: '',
    title: '',
    notes: '',
    team_groups: [],
    send_email_to_client: true
  };
  
  const [newAppointment, setNewAppointment] = useState(initialAppointmentState);

  // Check URL params for Google Calendar connection
  useEffect(() => {
    if (searchParams.get('google_connected') === 'true') {
      toast.success('Google Calendar connesso con successo!');
      setGoogleConnected(true);
      window.history.replaceState({}, '', '/calendar');
    }
    const googleError = searchParams.get('google_error');
    if (googleError) {
      // Decode error message if it's not just 'true'
      const errorMessage = googleError === 'true' 
        ? 'Errore nella connessione a Google Calendar' 
        : decodeURIComponent(googleError);
      toast.error(errorMessage, { duration: 8000 });
      window.history.replaceState({}, '', '/calendar');
    }
  }, [searchParams]);

  // Check Google Calendar connection status
  useEffect(() => {
    const checkGoogleStatus = async () => {
      try {
        const res = await googleCalendarApi.getStatus();
        setGoogleConnected(res.data.connected);
      } catch (error) {
        console.error('Error checking Google status:', error);
      }
    };
    checkGoogleStatus();
  }, []);

  // Get visible users based on role
  const getVisibleUsers = () => {
    if (!allUsers.length) return [];
    
    if (isAdmin) {
      return allUsers;
    } else if (isProjectManager) {
      return allUsers.filter(u => 
        ['admin', 'external_technician', 'furniture_sales', 'project_manager'].includes(u.role)
      );
    } else {
      return allUsers.filter(u => u.id === user?.id);
    }
  };

  useEffect(() => {
    loadData();
  }, [currentDate, viewMode, selectedCalendarUser]);

  const loadData = async () => {
    try {
      // Calculate date range based on view mode
      let start, end;
      switch (viewMode) {
        case 'day':
          start = new Date(currentDate);
          start.setHours(0, 0, 0, 0);
          end = new Date(currentDate);
          end.setHours(23, 59, 59, 999);
          break;
        case 'week':
          start = startOfWeek(currentDate, { weekStartsOn: 1 });
          end = endOfWeek(currentDate, { weekStartsOn: 1 });
          break;
        case 'month':
        default:
          start = startOfMonth(currentDate);
          end = endOfMonth(currentDate);
          break;
      }
      
      const appointmentParams = { 
        start_date: start.toISOString(), 
        end_date: end.toISOString() 
      };
      
      if (selectedCalendarUser && selectedCalendarUser !== 'all') {
        appointmentParams.technician_id = selectedCalendarUser;
      }
      
      const [aptRes, roomsRes, leadsRes, techRes] = await Promise.all([
        appointmentApi.getAll(appointmentParams),
        roomApi.getAll(),
        leadApi.getAll(),
        userApi.getTechnicians()
      ]);
      // /api/users è admin-only: per i tecnici torna 403 -> non deve far fallire
      // l'intero caricamento del calendario (serve solo al filtro "tutti gli utenti").
      let usersData = [];
      try {
        const usersRes = await userApi.getAll();
        usersData = usersRes.data;
      } catch (e) {
        usersData = [];
      }

      setAppointments(aptRes.data);
      setRooms(roomsRes.data);
      setLeads(leadsRes.data);
      setTechnicians(techRes.data);
      setAllUsers(usersData);
      
      // Load Google Calendar events if connected
      if (googleConnected) {
        try {
          const googleRes = await googleCalendarApi.getEvents({
            start_date: start.toISOString(),
            end_date: end.toISOString()
          });
          setGoogleEvents(googleRes.data || []);
        } catch (googleError) {
          console.warn('Error loading Google events:', googleError);
          setGoogleEvents([]);
        }
      } else {
        // Google not connected - no external events to show
        // When connected, events will be loaded from /api/google-calendar/events
        // Deduplication: events with id matching appointment.google_event_id are filtered out
        setGoogleEvents([]);
      }
      
      // Update currentMonth for backwards compatibility
      setCurrentMonth(currentDate);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAppointment = async () => {
    if (['sopralluogo', 'sede', 'scelta_extra'].includes(newAppointment.appointment_type) && !newAppointment.lead_id) {
      toast.error('Seleziona un cliente');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...newAppointment,
        appointment_date: newAppointment.appointment_date.toISOString(),
        assigned_technician_id: newAppointment.assigned_technician_id || (isTechnician ? user?.id : null),
        room_id: newAppointment.room_id || null
      };
      
      await appointmentApi.create(payload);
      toast.success('Appuntamento creato');
      setShowNewAppointmentModal(false);
      setNewAppointment(initialAppointmentState);
      await loadData();
    } catch (error) {
      console.error('Error creating appointment:', error);
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (apt) => {
    setSelectedAppointment(apt);
    setEditData({
      appointment_date: new Date(apt.appointment_date),
      send_email: true,
      reason: ''
    });
    setShowEditModal(true);
  };

  const openCancelModal = (apt) => {
    setSelectedAppointment(apt);
    setCancelData({ send_email: true, reason: '' });
    setShowCancelModal(true);
  };

  const handleEditAppointment = async () => {
    if (!selectedAppointment) return;
    
    setSaving(true);
    try {
      await appointmentApi.reschedule(selectedAppointment.id, {
        new_date: editData.appointment_date.toISOString(),
        reason: editData.reason,
        send_email: editData.send_email
      });
      toast.success('Appuntamento modificato');
      setShowEditModal(false);
      setSelectedAppointment(null);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella modifica');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelAppointment = async () => {
    if (!selectedAppointment) return;
    
    setSaving(true);
    try {
      await appointmentApi.cancel(selectedAppointment.id, {
        reason: cancelData.reason,
        send_email: cancelData.send_email
      });
      toast.success('Appuntamento annullato');
      setShowCancelModal(false);
      setSelectedAppointment(null);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'annullamento');
    } finally {
      setSaving(false);
    }
  };

  const handleSyncToGoogle = async (appointmentId) => {
    setSyncingToGoogle(appointmentId);
    try {
      await googleCalendarApi.syncAppointment(appointmentId);
      toast.success('Sincronizzato con Google Calendar');
      await loadData();
    } catch (error) {
      toast.error('Errore nella sincronizzazione');
    } finally {
      setSyncingToGoogle(null);
    }
  };

  const handleSyncAllToGoogle = async () => {
    setSyncingAll(true);
    try {
      const result = await googleCalendarApi.syncAll();
      toast.success(`Sincronizzati ${result.data.synced_count} appuntamenti`);
      await loadData();
    } catch (error) {
      toast.error('Errore nella sincronizzazione');
    } finally {
      setSyncingAll(false);
    }
  };

  const handleConnectGoogle = async () => {
    try {
      const res = await googleCalendarApi.getConnectUrl();
      window.location.href = res.data.auth_url;
    } catch (error) {
      console.error('Google Calendar connect error:', error);
      toast.error(error.response?.data?.detail || 'Errore nella connessione a Google');
    }
  };

  const handleDisconnectGoogle = async () => {
    if (!window.confirm('Disconnettere Google Calendar?')) return;
    try {
      await googleCalendarApi.disconnect();
      setGoogleConnected(false);
      toast.success('Google Calendar disconnesso');
    } catch (error) {
      toast.error('Errore nella disconnessione');
    }
  };

  const handleSendReminder = async (appointmentId) => {
    setSendingReminder(appointmentId);
    try {
      await appointmentApi.sendReminder(appointmentId);
      toast.success('Promemoria inviato');
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio del promemoria');
    } finally {
      setSendingReminder(null);
    }
  };

  // Navigation handlers based on view mode
  const handlePrev = () => {
    switch (viewMode) {
      case 'day':
        setCurrentDate(subDays(currentDate, 1));
        break;
      case 'week':
        setCurrentDate(subWeeks(currentDate, 1));
        break;
      case 'month':
      default:
        setCurrentDate(subMonths(currentDate, 1));
        break;
    }
  };

  const handleNext = () => {
    switch (viewMode) {
      case 'day':
        setCurrentDate(addDays(currentDate, 1));
        break;
      case 'week':
        setCurrentDate(addWeeks(currentDate, 1));
        break;
      case 'month':
      default:
        setCurrentDate(addMonths(currentDate, 1));
        break;
    }
  };

  // Event click handler - opens detail panel
  const handleEventClick = (appointment) => {
    setDetailAppointment(appointment);
    setShowDetailPanel(true);
  };

  // Time slot click handler - opens new appointment modal with pre-filled date/time
  const handleTimeSlotClick = (date, hour) => {
    const newDate = new Date(date);
    newDate.setHours(hour, 0, 0, 0);
    setNewAppointment({
      ...initialAppointmentState,
      appointment_date: newDate,
    });
    setSelectedDate(date);
    setShowNewAppointmentModal(true);
  };

  // Navigate to lead detail
  const handleNavigateToLead = (leadId) => {
    setShowDetailPanel(false);
    navigate(`/leads/${leadId}`);
  };

  // Get technician info for detail panel
  const getAppointmentTechnician = (apt) => {
    if (!apt?.assigned_technician_id) return null;
    return allUsers.find(u => u.id === apt.assigned_technician_id);
  };

  // Get lead info for detail panel
  const getAppointmentLead = (apt) => {
    if (!apt?.lead_id) return null;
    return leads.find(l => l.id === apt.lead_id);
  };

  // Normalize and merge CRM appointments with Google events
  // Deduplicate: if a Google event ID matches an appointment's google_event_id, show only CRM
  const mergedCalendarEvents = React.useMemo(() => {
    // Create a set of Google event IDs that are already linked to CRM appointments
    const linkedGoogleEventIds = new Set(
      appointments
        .filter(apt => apt.google_event_id)
        .map(apt => apt.google_event_id)
    );

    // Normalize CRM appointments
    const normalizedCRM = appointments.map(apt => ({
      ...apt,
      source: 'crm',
      calendar_id: `crm-${apt.id}`,
      start: apt.appointment_date,
      end: apt.appointment_date ? new Date(new Date(apt.appointment_date).getTime() + (apt.duration_minutes || 60) * 60000).toISOString() : null,
    }));

    // Normalize Google events (only those NOT linked to CRM)
    const normalizedGoogle = (googleEvents || [])
      .filter(event => !linkedGoogleEventIds.has(event.id))
      .map(event => {
        // Parse Google event dates
        const startDateTime = event.start?.dateTime || event.start?.date;
        const endDateTime = event.end?.dateTime || event.end?.date;
        
        return {
          id: event.id,
          source: 'google',
          calendar_id: `google-${event.id}`,
          google_event_id: event.id,
          title: event.summary || 'Evento Google',
          appointment_date: startDateTime,
          start: startDateTime,
          end: endDateTime,
          location: event.location || '',
          notes: event.description || '',
          appointment_type: 'google_external',
          status: event.status === 'cancelled' ? 'annullato' : 'confermato',
          // Google event link
          htmlLink: event.htmlLink,
          // For display
          duration_minutes: startDateTime && endDateTime 
            ? Math.round((new Date(endDateTime) - new Date(startDateTime)) / 60000)
            : 60,
        };
      });

    // Merge and sort by start time
    return [...normalizedCRM, ...normalizedGoogle].sort((a, b) => {
      const dateA = new Date(a.start || a.appointment_date);
      const dateB = new Date(b.start || b.appointment_date);
      return dateA - dateB;
    });
  }, [appointments, googleEvents]);

  // Helper functions
  const getAppointmentTypeColor = (type, source) => {
    if (source === 'google') return 'bg-[#DB4437]'; // Google Calendar rosso
    switch (type) {
      case 'sopralluogo': return 'bg-[#4A90A4]';  // Teal - coerente con altre viste
      case 'sede': return 'bg-[#5B9BD5]';         // Blu chiaro
      case 'presentazione': return 'bg-[#5B9BD5]';
      case 'interno': return 'bg-[#7F7F7F]';      // Grigio
      case 'personale': return 'bg-[#7F7F7F]';
      case 'scelta_extra': return 'bg-[#70AD47]'; // Verde
      case 'scadenza_contabile': return 'bg-[#DC2626]';
      case 'google_external': return 'bg-[#DB4437]';
      default: return 'bg-[#7F7F7F]';
    }
  };

  const getAppointmentTypeLabel = (type) => {
    switch (type) {
      case 'sopralluogo': return 'Sopralluogo';
      case 'sede': return 'In Sede';
      case 'interno': return 'Interno';
      case 'scelta_extra': return 'Scelta Extra';
      case 'scadenza_contabile': return 'Scadenza';
      case 'personale': return 'Personale';
      default: return type;
    }
  };

  const getRoomName = (roomId) => {
    const room = rooms.find(r => r.id === roomId);
    return room ? room.name : '';
  };

  // Permesso dinamico per creare appuntamenti (dalla matrice db.permissions)
  // Admin è sempre autorizzato. Gli altri ruoli devono avere 'create_appointments' ON
  const canCreateAppointment = hasPermission('create_appointments');
  const visibleUsers = getVisibleUsers();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF7819]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="calendar-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-h2">Calendario</h1>
          <p className="text-body-small mt-1">Gestione appuntamenti</p>
        </div>
      </div>

      {/* Calendar Header with controls */}
      <CalendarHeader
        currentDate={currentDate}
        currentMonth={currentMonth}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onPrev={handlePrev}
        onNext={handleNext}
        onPrevMonth={() => setCurrentDate(subMonths(currentDate, 1))}
        onNextMonth={() => setCurrentDate(addMonths(currentDate, 1))}
        selectedCalendarUser={selectedCalendarUser}
        setSelectedCalendarUser={setSelectedCalendarUser}
        visibleUsers={visibleUsers}
        googleConnected={googleConnected}
        onConnectGoogle={handleConnectGoogle}
        onDisconnectGoogle={handleDisconnectGoogle}
        onSyncAllToGoogle={handleSyncAllToGoogle}
        syncingAll={syncingAll}
        canCreateAppointment={canCreateAppointment}
        onNewAppointment={() => setShowNewAppointmentModal(true)}
        isAdmin={isAdmin}
        isProjectManager={isProjectManager}
      />

      {/* Legend */}
      <div className="flex justify-end">
        <CalendarLegend compact={true} />
      </div>

      {/* Calendar View based on viewMode */}
      {viewMode === 'day' && (
        <CalendarDayView
          currentDate={currentDate}
          appointments={mergedCalendarEvents}
          leads={leads}
          onEventClick={handleEventClick}
          onTimeSlotClick={handleTimeSlotClick}
        />
      )}

      {viewMode === 'week' && (
        <CalendarWeekView
          currentDate={currentDate}
          appointments={mergedCalendarEvents}
          leads={leads}
          onEventClick={handleEventClick}
          onTimeSlotClick={handleTimeSlotClick}
        />
      )}

      {viewMode === 'month' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <CalendarGrid
            currentMonth={currentDate}
            selectedDate={selectedDate}
            setSelectedDate={(date) => {
              setSelectedDate(date);
              // Click on date in month view also works to see day details
            }}
            appointments={mergedCalendarEvents}
            leads={leads}
            getAppointmentTypeColor={getAppointmentTypeColor}
            getRoomName={getRoomName}
          />

          <CalendarDayDetails
            selectedDate={selectedDate}
            appointments={mergedCalendarEvents}
            leads={leads}
            technicians={technicians}
            rooms={rooms}
            googleConnected={googleConnected}
            isAdmin={isAdmin}
            isProjectManager={isProjectManager}
            syncingToGoogle={syncingToGoogle}
            sendingReminder={sendingReminder}
            onSyncToGoogle={handleSyncToGoogle}
            onSendReminder={handleSendReminder}
            onEditAppointment={openEditModal}
            onCancelAppointment={openCancelModal}
            getAppointmentTypeColor={getAppointmentTypeColor}
            getAppointmentTypeLabel={getAppointmentTypeLabel}
          />
        </div>
      )}

      {/* Appointment Detail Panel (Teamleader style) - CRM events */}
      {showDetailPanel && detailAppointment && detailAppointment.source !== 'google' && (
        <AppointmentDetailPanel
          appointment={detailAppointment}
          lead={getAppointmentLead(detailAppointment)}
          technician={getAppointmentTechnician(detailAppointment)}
          allAppointments={mergedCalendarEvents.filter(e => e.source !== 'google')}
          onClose={() => {
            setShowDetailPanel(false);
            setDetailAppointment(null);
          }}
          onEdit={(apt) => {
            setShowDetailPanel(false);
            openEditModal(apt);
          }}
          onCancel={(apt) => {
            setShowDetailPanel(false);
            openCancelModal(apt);
          }}
          onReschedule={(apt) => {
            setShowDetailPanel(false);
            openEditModal(apt);
          }}
          onNavigatePrev={(apt) => setDetailAppointment(apt)}
          onNavigateNext={(apt) => setDetailAppointment(apt)}
          onNavigateToLead={handleNavigateToLead}
        />
      )}

      {/* Google Event Detail Panel (Read-only) */}
      {showDetailPanel && detailAppointment && detailAppointment.source === 'google' && (
        <GoogleEventDetailPanel
          event={detailAppointment}
          onClose={() => {
            setShowDetailPanel(false);
            setDetailAppointment(null);
          }}
        />
      )}

      {/* New Appointment Modal */}
      <NewAppointmentModal
        open={showNewAppointmentModal}
        onOpenChange={setShowNewAppointmentModal}
        newAppointment={newAppointment}
        setNewAppointment={setNewAppointment}
        leads={leads}
        technicians={technicians}
        rooms={rooms}
        teamGroups={teamGroups}
        durationOptions={durationOptions}
        isAdmin={isAdmin}
        isProjectManager={isProjectManager}
        isTechnician={isTechnician}
        currentUserId={user?.id}
        onSubmit={handleCreateAppointment}
        saving={saving}
      />

      {/* Edit Appointment Modal */}
      <EditAppointmentModal
        open={showEditModal}
        onOpenChange={setShowEditModal}
        appointment={selectedAppointment}
        editData={editData}
        setEditData={setEditData}
        onSubmit={handleEditAppointment}
        saving={saving}
      />

      {/* Cancel Appointment Modal */}
      <CancelAppointmentModal
        open={showCancelModal}
        onOpenChange={setShowCancelModal}
        appointment={selectedAppointment}
        cancelData={cancelData}
        setCancelData={setCancelData}
        onSubmit={handleCancelAppointment}
        saving={saving}
      />
    </div>
  );
};

export default CalendarPage;
